import {useCallback, useEffect, useRef, useState} from 'react';
import {common, priority} from '@priority-software/priority-proto-api/lib';
import {promiseDebounce} from '../../_legacy/library/utils/debounce';
import {getFocusedIndex} from '../../components/Common/FieldSearch/utils/search';
import {getNextSortBy, getNextIsDesc} from '../../_legacy/FieldSearch/utils/sort';
import {sortResults} from '../../_legacy/library/utils/search';
import {keys, NAVIGATION_KEYS} from '../../constants/KeyCodes';
import {filterResults, SearchOptions} from '../../components/Common/FieldSearch/hooks/useSearch';
import {fieldSelect} from '../../services/form/form';
import {Column} from '../../stores/Form';
import {isRTL} from '../../Localization';
import {
    getFormSystemBooleanFlag,
    setFormSystemFlags
} from '../../features/Form/actions/formSystemValues';

export type Option = {
    string1?: string;
    string2?: string;
    retval: string;
};

enum SortBy {
    string1 = 'string1',
    string2 = 'string2'
}

export type RequestStateParams = {
    searchQuery: string;
    ignorecase: boolean;
    searchFromBeginning: boolean;
};

const engTexts = {description: 'Description'};

const heTexts = {description: 'תאור'};

export const texts = isRTL() ? heTexts : engTexts;

export type SearchState = RequestStateParams & {
    // API props
    hasMore: boolean;
    results: Array<Option> | null;
    type?: priority.netitems.shared.FieldSelectType;
    // hook props
    _results: Array<Option> | null;
    focusedIndex: number;
    isSearchable: boolean;
    isMulti: boolean;
    isScrollable: boolean;
    isSearching: boolean;
    isLoading: boolean;
    isDesc?: boolean;
    isScrolling: boolean;
    searchByTitle: string;
    sortBy?: SortBy;
    searchStart: string;
    isNoResults: boolean;
    Page?: number;
};

const initState = {
    searchQuery: '',
    results: [],
    _results: [],
    focusedIndex: -1,
    isSearchable: false,
    isMulti: false,
    isScrollable: true,
    searchFromBeginning: false,
    isSearching: false,
    ignorecase: false,
    isLoading: false,
    isScrolling: false,
    searchByTitle: '',
    searchStart: '',
    isNoResults: false,
    hasMore: false
};

const PAGE_SIZE = 5;

const initDropdownState = (
    search: priority.netitems.form.FieldSelectRs,
    searchQuery: string = '',
    column: Column
) => {
    // Since the API response doesn't bring `Type` if search doesn't have results we should handle type only when dropdown open
    const type = search.Type || priority.netitems.shared.FieldSelectType.Choose;
    const isSearchable = type !== priority.netitems.shared.FieldSelectType.Choose;
    const searchResults = parseSearchRs(search, searchQuery, isSearchable);
    const is2ColumnsLayout = !!searchResults.results[0]?.string2;
    return {
        // Base properties
        isMulti: search.Multi,
        title1: column.Title,
        isZoom: !!column.Zoom,
        title2: is2ColumnsLayout ? texts.description : '',
        direction: 'auto', // Todo: decide correct direction
        isSearchable,
        // Dynamic properties
        ...searchResults
    };
};

const parseSearchRs = (
    search: priority.netitems.form.FieldSelectRs,
    searchQuery: string = '',
    isSearchable
) => {
    const mappedResults = search.Values.map(item => ({
        string1: item.Value,
        string2: item.Description,
        string3: item.Description2,
        retval: item.Value
    }));
    return {
        hasMore: search.HaveMore,
        results: mappedResults,
        _results: mappedResults,
        focusedIndex: getFocusedIndex(mappedResults, searchQuery, isSearchable, search.Multi),
        isNoResults: search.Values.length === 0,
        isScrollable: !!search.HaveMore
    };
};

export const useSearch = (
    column: Column,
    options: SearchOptions = {showWildCardOptions: false, isForceMulti: false},
    preFetchedSearchState = {}
) => {
    const ColumnID = column?.id;
    const Session = column?.parentForm.session;

    const [state, dispatchState] = useState<SearchState>({
        ...initState,
        ...preFetchedSearchState,
        ignorecase: getFormSystemBooleanFlag('fieldSearchIgnoreCase'),
        searchFromBeginning: getFormSystemBooleanFlag('fieldSearchFromStart')
    });

    const setState = (partialState: Partial<SearchState>) =>
        dispatchState(state => ({...state, ...partialState}));

    const requestData = useCallback(
        async (defaultState: SearchState, updateParams: Partial<SearchState & {Page: number}>) => {
            const patchedState = {...defaultState, ...updateParams} as SearchState;
            const reqParams = {
                ColumnID,
                Session,
                Pattern: patchedState.searchQuery.trim(),
                FromStart: patchedState.searchFromBeginning,
                IgnoreCase: patchedState.ignorecase,
                Page: patchedState.Page || pageIndex.current,
                Preferred: patchedState.isSearchable
                    ? priority.netitems.shared.FieldSelectType.SearchAll
                    : undefined
            };
            return await fieldSelect(reqParams);
        },
        [Session, ColumnID]
    );

    const onSearchAction = useCallback(promiseDebounce(requestData, 400), []);

    /****  OPEN ****/
    const handleSearchOpen = useCallback(
        async (
            searchQuery: string = '',
            isForceSearch: boolean = false,
            isFilterChooseResults = false,
            callback?: (state) => void
        ) => {
            setState({isLoading: true, searchQuery});

            const search = await requestData(state, {searchQuery, isSearchable: isForceSearch});
            const initialStateOnOpen = initDropdownState(search, searchQuery, column);

            setState({isLoading: false, ...initialStateOnOpen});

            if (callback) {
                callback(initialStateOnOpen);
            }
        },
        [state, column, requestData]
    );

    /****  SEARCH CHANGE ****/
    const handleSearchChange = useCallback(
        async (searchQuery: string) => {
            const {isSearchable, isMulti} = state;

            if (isSearchable) {
                setState({isSearching: true, searchQuery});
                const search: any = await onSearchAction(state, {searchQuery});
                setState({
                    isSearching: false,
                    ...parseSearchRs(search, searchQuery, state.isSearchable)
                });
            } else if (!isSearchable) {
                const {_results} = state;
                const results = filterResults(_results, searchQuery);

                setState({
                    results,
                    searchQuery,
                    focusedIndex: getFocusedIndex(results, searchQuery, isSearchable, isMulti)
                });
            } else {
                const {results} = state;
                setState({
                    ...state,
                    searchQuery,
                    focusedIndex: getFocusedIndex(results, searchQuery, isSearchable, isMulti)
                });
            }
        },
        [state, onSearchAction]
    );

    /****  SCROLL ****/
    const pageIndex = useRef(0);

    useEffect(() => {
        // reset page index
        pageIndex.current = 0;
    }, [
        state.isLoading,
        state.searchByTitle,
        state.searchFromBeginning,
        state.ignorecase,
        state.searchQuery,
        state.sortBy
    ]);

    const handleScroll = useCallback(
        async (nextFocusedIndex = state.focusedIndex) => {
            if (state.hasMore && state.isScrollable && !state.isScrolling) {
                setState({isScrolling: true});
                const search = await requestData(state, {
                    Page: ++pageIndex.current,
                    searchQuery: state.searchQuery
                });
                const serializedSearch = parseSearchRs(
                    search,
                    state.searchQuery,
                    state.isSearchable
                );
                const nextResults = serializedSearch.results;
                const {results} = state;
                setState({
                    isScrolling: false,
                    ...serializedSearch,
                    focusedIndex: nextFocusedIndex,
                    results: results.concat(nextResults)
                });
            }
        },
        [state, requestData]
    );

    /****  SEARCH ACTION ****/

    const __handleSearchAction = useCallback(
        async (params: Partial<SearchState>) => {
            if (!state.isSearching) {
                setState({isSearching: true, ...params});
                const search: any = await onSearchAction(state, params);
                setState({
                    isSearching: false,
                    ...params,
                    ...parseSearchRs(
                        search,
                        params.searchQuery || state.searchQuery,
                        state.isSearchable
                    )
                });
            }
        },
        [state, onSearchAction]
    );

    /****  SORT ****/
    const handleSortByChange = useCallback(
        (sortKey: SortBy) => {
            const {sortBy, isDesc, results, _results, searchQuery, isSearchable, isMulti} = state;
            const nextIsDesc = getNextIsDesc(sortKey, sortBy, isDesc);
            const nextKey = getNextSortBy(sortKey, sortBy, isDesc);
            const sortedRes = nextKey ? sortResults(results, nextKey, nextIsDesc) : _results;

            setState({
                results: sortedRes,
                sortBy: nextKey,
                isDesc: nextIsDesc,
                focusedIndex: getFocusedIndex(sortedRes, searchQuery, isSearchable, isMulti)
            });
        },
        [state]
    );

    const handleIgnoreCaseChange = async () => {
        const ignorecase = !state.ignorecase;
        setFormSystemFlags({
            fieldSearchIgnoreCase: new common.Constant({Boolean: ignorecase})
        });
        return await __handleSearchAction({ignorecase});
    };

    const handleSearchFromBeginningChange = useCallback(async () => {
        const FromStart = !state.searchFromBeginning;
        setFormSystemFlags({
            fieldSearchFromStart: new common.Constant({Boolean: FromStart})
        });
        return await __handleSearchAction({searchFromBeginning: FromStart});
    }, [state.searchFromBeginning, __handleSearchAction]);

    const handleSearchByChange = (searchQuery: string = state.searchQuery) =>
        __handleSearchAction({searchQuery});

    const handleNavigationKeys = (event: KeyboardEvent) => {
        if (NAVIGATION_KEYS.includes(event.key)) {
            event.stopPropagation();
            const {focusedIndex, results, isMulti, isScrollable} = state;
            const topFocusedIndex = isMulti ? -1 : 0;
            const bottomFocusedIndex = results.length - 1;
            let nextFocusedIndex = focusedIndex;
            switch (event.key) {
                case keys.ARROW_UP:
                    event.preventDefault();
                    nextFocusedIndex =
                        focusedIndex <= topFocusedIndex ? bottomFocusedIndex : focusedIndex - 1;

                    break;
                case keys.ARROW_DOWN:
                    event.preventDefault();
                    nextFocusedIndex =
                        focusedIndex === bottomFocusedIndex && !isScrollable
                            ? topFocusedIndex
                            : focusedIndex + 1;

                    break;
                case keys.PAGE_UP:
                    event.preventDefault();
                    if (focusedIndex - PAGE_SIZE > topFocusedIndex) {
                        nextFocusedIndex = focusedIndex - PAGE_SIZE;
                    } else {
                        nextFocusedIndex = 0;
                    }

                    break;
                case keys.PAGE_DOWN:
                    event.preventDefault();
                    if (focusedIndex + PAGE_SIZE >= bottomFocusedIndex && !isScrollable) {
                        nextFocusedIndex = bottomFocusedIndex;
                    } else {
                        nextFocusedIndex = focusedIndex + PAGE_SIZE;
                    }

                    break;
                case keys.HOME:
                    if (!state.isSearchable) {
                        event.preventDefault();
                        nextFocusedIndex = topFocusedIndex;
                    }
                    break;
                case keys.END:
                    if (!state.isSearchable) {
                        event.preventDefault();
                        nextFocusedIndex = bottomFocusedIndex;
                    }
                    break;
            }
            if (isScrollable && nextFocusedIndex >= bottomFocusedIndex) {
                handleScroll(nextFocusedIndex);
            } else {
                setState({focusedIndex: nextFocusedIndex});
            }
        }
    };

    const handleSearchQueryChange = (searchQuery: string = '') => {
        setState({searchQuery});
    };

    const handlers = {
        handleSearchOpen,
        handleSearchChange,
        handleScroll,
        handleSortByChange,
        handleIgnoreCaseChange,
        handleSearchByChange,
        handleSearchFromBeginningChange,
        handleNavigationKeys,
        handleSearchQueryChange
    };

    return [state, handlers] as [SearchState, SearchHandlers];
};

export interface SearchHandlers {
    handleSearchOpen(
        searchQuery?: string,
        isForceSearch?: boolean,
        isFilterChooseResults?: boolean,
        callback?: (state: SearchState) => void
    ): Promise<any>;

    handleSearchChange(searchQuery?: string): Promise<any>;

    handleScroll(): Promise<any>;

    handleSortByChange(sortBy: string): void;

    handleNavigationKeys(event: KeyboardEvent): void;

    handleIgnoreCaseChange(): Promise<any>;

    handleSearchFromBeginningChange(): Promise<any>;

    handleSearchByChange(searchQuery?: string): Promise<any>;

    handleSearchQueryChange(searchQuery?: string): void;
}
